import React from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';

import NotFound from '../pages/NotFound/NotFound';
import Contact from '../pages/Compliancy/Contact';
import PrivacyPolicy from '../pages/Compliancy/PrivacyPolicy';
import TermsOfUse from '../pages/Compliancy/TermsOfUse';
import Header from '../pages/Home/Header';
import Subscriptions from '../pages/Subscriptions/Subscriptions';
import ForTutoring from '../pages/ForTutoring/ForTutoring';
import Features from '../pages/Features/Features';
import Onboarding from '../pages/Onboarding/Onboarding';
import { CONTACT_US_LINK, LINK_WHITEBOARD, PROFILE_LINK, SPECIAL_QUOTA_LINK } from '../common/urlLinks';
import { startProviderLogin } from '../common/utils';
import SpecialQuota from '../pages/Compliancy/SpecialQuota';
import HomeLanding from '../pages/Home/HomeLanding/HomeLanding';
import { COLORS2 } from '../constants';
import RedesignFooter from '../MyComponents/Footer/RedesignFooter';
import Tutorial from '../pages/Tutorial/Tutorial';
import { ResponsiveLoginModal } from '../pages/Home/HomeModals/ResponsiveLoginModal';
import PageMeta from '../MyComponents/LandingSections/PageMeta';
import ForSchools from '../pages/ForSchools/ForSchools';
import SignUpConsent from '../pages/Compliancy/SignUpConsent';
import { Unsubscribe } from '../pages/Unsubscribe/Unsubscribe';
import About from '../pages/About/About';
import Snackbar from '@material-ui/core/Snackbar';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Alert } from '@material-ui/lab';
import store from '../redux/store.js';
import { hideNotification } from '../redux/actions.js';
import LinkWhiteboard from '../pages/LinkWhiteboard/LinkWhiteboard';

const wrapperStyle = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    // `clip` rather than `hidden`: hidden makes this element a scroll container,
    // which stops the redesigned sticky nav from pinning to the viewport. `clip`
    // still suppresses the horizontal overflow this was added for.
    overflowX: 'clip',
    backgroundColor: COLORS2.dawnBackground,
};


function UnauthenticatedApp({ language, updateLanguage, appState, intl }) {
    const [open, setOpen] = React.useState(false);
    const [openFromHeader, setOpenFromHeader] = React.useState(true);
    let history = useHistory();
    // Stable identities: the onboarding page opens the modal from an effect
    // that lists handleOpen as a dependency. A fresh function on every render
    // re-ran that effect after each close, so the modal reopened at once and
    // could not be dismissed.
    const handleOpen = React.useCallback((fromHeader = true) => {
        setOpen(true);
        setOpenFromHeader(fromHeader);
    }, []);

    React.useEffect(() => {
        // opened via a redirect from a protected page (e.g. a lesson link) so
        // the visitor can pick an OAuth provider or continue as guest
        if (new URLSearchParams(window.location.search).get('showLogin')) {
            handleOpen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleClose = React.useCallback(() => {
        setOpen(false);
        window.localStorage.removeItem('pathBeforeLogin');
    }, []);

    const handleLogin = (target = '') => {
        if (target === 'linkWhiteboard') {
            // The only branch that routes client-side instead of doing a full
            // page load, so it is also the only one that has to dismiss the
            // modal itself - otherwise it stays open over /linkWhiteboard.
            setOpen(false);
            history.push(LINK_WHITEBOARD);
            return;
        }
        if (target === '/microsoft' && window.isTeams) {
            window.microsoftTeams.getContext(() => {
                window.microsoftTeams.authentication.authenticate({
                    successCallback: () => {
                        window.location.reload();
                    },
                    failureCallback: () => {
                    },
                    url: window.location.protocol + '//' + window.location.host + '/login/microsoft-teams',
                });
            });
            return;
        }
        startProviderLogin(target);
    };

    const handleCloseNotif = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        store.dispatch(hideNotification());
    };

    let notifMessage = '';
    if (appState.notificationMessage) {
        if (typeof appState.notificationMessage === 'string') {
            notifMessage = intl.formatMessage({
                id: 'notification.code.' + appState.notificationMessage,
            });
        } else {
            notifMessage = intl.formatMessage(
                {
                    id: 'notification.code.' + appState.notificationMessage.messageKey,
                },
                appState.notificationMessage.data,
            );
        }
    }
    return (
        <div style={wrapperStyle}>
            {appState.isNotificationOpen ? (
                <Snackbar
                    open={appState.isNotificationOpen}
                    autoHideDuration={6000}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <Alert onClose={handleCloseNotif} severity={appState.notificationType}>
                        {notifMessage}
                    </Alert>
                </Snackbar>
            ) : null}

            {/* wrap='nowrap': a Grid container defaults to flex-wrap: wrap, which
                on a column direction makes this a multi-line flex container. That
                packs the page into columns and compresses sections whose height
                comes from a ratio - it was clipping the video section to a third
                of its height. No spacing: it only gave this container negative
                8px margins and a width of 100% + 16px, i.e. sideways overflow that
                browsers without overflow-x: clip let a touch drag pan. */}
            <Grid container direction='column' wrap='nowrap' style={{ flex: 1 }}>
                <PageMeta />
                <Header updateLanguage={updateLanguage} handleOpen={handleOpen} language={language} />
                <Switch>
                    <Route exact={true} path={['/', '/en', '/fr', '/ro']}>
                        <HomeLanding
                            handleOpen={handleOpen}
                            open={open}
                            handleLogin={handleLogin}
                            language={language}
                        />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>
                    <Route exact={true} path={CONTACT_US_LINK}>
                        <Contact />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>
                    <Route exact={true} path={SPECIAL_QUOTA_LINK}>
                        <SpecialQuota />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>

                    <Route exact={true} path='/privacypolicy'>
                        <PrivacyPolicy />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>
                    <Route exact={true} path='/terms'>
                        <TermsOfUse />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>
                    <Route exact={true} path={['/ro/tutorial', '/fr/tutorial', '/en/tutorial', '/tutorial']}>
                        <Tutorial language={language} />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>
                    <Route
                        exact={true}
                        path={['/subscriptions', '/ro/subscriptions', '/fr/subscriptions', '/en/subscriptions']}
                    >
                        <Subscriptions language={language} />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>
                    <Route path={['/features', '/ro/features', '/fr/features', '/en/features']}>
                        <Features />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>
                    <Route exact={true} path={['/ro/tutoring', '/fr/tutoring', '/en/tutoring', '/tutoring']}>
                        <ForTutoring language={language} />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>
                    <Route
                        exact={false}
                        path={[
                            '/ro/about/:aboutValue',
                            '/fr/about/:aboutValue',
                            '/en/about/:aboutValue',
                            '/about/:aboutValue',
                            '/ro/about',
                            '/fr/about',
                            '/en/about',
                            '/about',
                        ]}
                    >
                        <About />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>
                    <Route exact={true} path={['/ro/schools', '/fr/schools', '/en/schools', '/schools']}>
                        <ForSchools language={language} />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>

                    <Route exact={true} path='/onboarding'>
                        <Onboarding
                            handleLogin={handleLogin}
                            open={open}
                            handleOpen={handleOpen}
                            setOpen={setOpen}
                            handleClose={handleClose}
                            language={language}
                        />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>
                    <Route exact={true} path={LINK_WHITEBOARD}>
                        <LinkWhiteboard />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>

                    <Route path='/signup_consent' component={SignUpConsent} />
                    <Route path='/unsubscribe/:token' component={Unsubscribe} />

                    <Route exact={true} path='*'>
                        <NotFound />
                        <RedesignFooter handleOpen={handleOpen} />
                    </Route>
                </Switch>
                <ResponsiveLoginModal
                    open={open}
                    handleClose={handleClose}
                    handleLogin={handleLogin}
                    fromHeader={openFromHeader}
                />
            </Grid>
        </div>
    );
}

const mapStateToProps = (state) => {
    const { appState } = state;
    return { appState };
};

export default injectIntl(connect(mapStateToProps)(UnauthenticatedApp));